import { NO_CONNECTION_MESSAGE } from "@/offline/sync/hints";

/**
 * Strikte ISO-Kalenderdaten (YYYY-MM-DD).
 * Kein Date.parse / lose String-Interpretation.
 * Jahre 0–99 nicht über Date.UTC (das mappt sie auf 1900–1999), sondern setUTCFullYear.
 */

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Funddaten vor diesem Jahr sind unzulässig (Fehler, Speichern gesperrt). */
export const FOUND_ON_MIN_YEAR = 1900;

/** Frühester zulässiger Kalendertag, auch als native minimumDate. */
export const FOUND_ON_MIN_ISO = "1900-01-01";

/** Funddaten vor diesem Jahr gelten als sehr unplausibel (Warnung, kein Block). */
export const FOUND_ON_WARN_BEFORE_YEAR = 1990;

export const FOUND_ON_BEFORE_MIN_YEAR_ERROR =
  "Das Funddatum darf nicht vor 1900 liegen.";

/** Feldmeldung für jedes Datumsfeld, auch später in DeathClosureFields. */
export const DATE_BEFORE_MIN_YEAR_ERROR =
  "Das Datum darf nicht vor 1900 liegen.";

/** Generische Feldmeldung. Fund- und Todesdatum haben eigene Texte. */
export const DATE_IN_FUTURE_ERROR = "Das Datum darf nicht in der Zukunft liegen.";

export const FOUND_ON_FUTURE_ERROR =
  "Das Funddatum darf nicht in der Zukunft liegen.";

const INVALID_DATE_ERROR = "Ungültiges Datum. Bitte ein Kalenderdatum wählen.";
const FUTURE_DATE_ERROR = DATE_IN_FUTURE_ERROR;

/**
 * Bekannte englische RPC- und Trigger-Texte.
 * Längere, speziellere Nadeln stehen zuerst. Der Client zeigt die deutsche Zeile,
 * bis die Servermeldung selbst deutsch ist.
 */
const SERVER_MESSAGE_RULES: readonly [string, string][] = [
  ["found_on must not be in the future", FOUND_ON_FUTURE_ERROR],
  [
    "invalid found_on: expected YYYY-MM-DD calendar date",
    "Ungültiges Funddatum. Bitte ein Kalenderdatum wählen.",
  ],
  ["found_place_id unknown", "Der Fundort ist unbekannt."],
  [
    "care_site_id required for housing update",
    "Für die Unterbringung ist eine Pflegestelle nötig.",
  ],
  ["no access to care site", "Kein Zugriff auf diese Pflegestelle."],
  ["species free text too long", "Die Art darf höchstens 120 Zeichen haben."],
  [
    "care site not in organization",
    "Die Pflegestelle gehört nicht zu dieser Organisation.",
  ],
  [
    "place must be active occupiable node of care site",
    "Der Platz muss ein aktiver, belegbarer Ort der Pflegestelle sein.",
  ],
  ["invalid intake reason", "Ungültiger Aufnahmegrund."],
  ["no write access", "Keine Schreibberechtigung."],
  ["not authenticated", "Nicht angemeldet."],
  ["case not found", "Fall nicht gefunden."],
  ["client_entry_id required", "Die Eintrags-ID fehlt."],
  ["recorded_at required", "Der Zeitpunkt fehlt."],
  ["at least one event required", "Mindestens ein Ereignis ist nötig."],
  ["invalid diagnosis", "Ungültige Diagnose."],
  ["case has no care site", "Dem Fall ist keine Pflegestelle zugeordnet."],
  [
    "place must belong to current care site",
    "Der Platz gehört nicht zur aktuellen Pflegestelle.",
  ],
  [
    "use closure events for terminal statuses",
    "Abschluss bitte über die Abschlussaktion.",
  ],
  ["media asset not pending for case", "Das Bild ist diesem Fall nicht zugeordnet."],
  ["invalid medication", "Ungültiges Medikament."],
  ["free_name or medication_id required", "Name oder Medikament fehlt."],
  [
    "plan_revision_id required for planned dose",
    "Für die geplante Gabe fehlt die Planversion.",
  ],
  [
    "occurrence_key and planned_at required for planned dose",
    "Für die geplante Gabe fehlen Zeitpunkt und Vorgang.",
  ],
  ["invalid treatment", "Ungültige Behandlung."],
  ["free_name or treatment_id required", "Name oder Behandlung fehlt."],
  [
    "plan_revision_id required for planned treatment",
    "Für die geplante Behandlung fehlt die Planversion.",
  ],
  [
    "medication_id or treatment_id required to open plan",
    "Zum Öffnen des Plans fehlt Medikament oder Behandlung.",
  ],
  ["invalid therapy catalog ref", "Ungültiger Katalogeintrag."],
  ["active plan not found", "Kein aktiver Plan gefunden."],
  ["invalid plan_change action", "Ungültige Planänderung."],
  ["active hint not found", "Kein aktiver Hinweis gefunden."],
  ["invalid care_hint_change action", "Ungültige Hinweisänderung."],
  ["unsupported event type", "Dieser Ereignistyp wird nicht unterstützt."],
  ["care_read_full required", "Für diese Aktion fehlt die Pflegeberechtigung."],
  [
    "place_conflict: expected_from_place_id does not match current place",
    "Der Platz hat sich geändert. Bitte neu laden.",
  ],
  [
    "diagnosis_conflict: expected no open episode",
    "Die Diagnose ist bereits erfasst.",
  ],
  [
    "diagnosis_conflict: expected_from_state does not match current episode",
    "Der Diagnosestatus hat sich geändert. Bitte neu laden.",
  ],
  [
    "occurrence_conflict: duplicate regular occurrence",
    "Diese regelmäßige Gabe ist schon erfasst.",
  ],
  ["finder_pii required", "Keine Berechtigung für Finder-Kontaktdaten."],
  [
    "Das Todesdatum darf nicht in der Zukunft liegen.",
    "Das Todesdatum darf nicht in der Zukunft liegen.",
  ],
  [
    "Das Todesdatum darf nicht vor dem Funddatum liegen.",
    "Das Todesdatum darf nicht vor dem Funddatum liegen.",
  ],
  ["Das Todesdatum ist ungültig.", "Das Todesdatum ist ungültig."],
  [
    "Das Funddatum darf nicht nach dem Todesdatum liegen.",
    "Das Funddatum darf nicht nach dem Todesdatum liegen.",
  ],
  ["display_name required", "Der Name fehlt."],
  ["organization mismatch", "Der Kontakt gehört nicht zu dieser Organisation."],
  ["organization required", "Die Organisation fehlt."],
  ["contact not found", "Kontakt nicht gefunden."],
  ["roles required", "Mindestens eine Rolle ist nötig."],
];

/** Servertexte, die in der Oberfläche deutsch erscheinen. */
export function mapDateServerMessage(message: string): string {
  for (const [needle, german] of SERVER_MESSAGE_RULES) {
    if (message.includes(needle)) return german;
  }
  return message;
}

function readErrorMessage(error: unknown): string {
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  if (typeof error === "object" && error !== null && "message" in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string") return message;
  }
  return "";
}

const GERMAN_WORD =
  /\b(der|die|das|den|dem|ein|eine|kein|keine|für|und|oder|nicht|ist|sind|wird|wurde|werden|bitte|fehlt|fehlen|fehlgeschlagen|ungültig\w*|bereits|schon|kontakt|mindestens|zeichen|passwort|speichern|einladung|abgelaufen)\b/i;

const GERMAN_UI_TEXT = new RegExp(`${GERMAN_WORD.source}|[äöüÄÖÜß]`, "i");

/** Englische Fehlerwörter. Gilt nur für die Spracherkennung, nicht für die Queue. */
const ENGLISH_ERROR_WORD =
  /\b(error|failed|failure|invalid|required|missing|violates|constraint|duplicate|denied|exception|syntax|column|relation|already|exists|permission|fetch|network|timeout|aborted|canceling|cancelled|canceled)\b/i;

/** Namen in Anführungszeichen oder Klammern können Umlaute tragen, ohne dass der Satz deutsch ist. */
function withoutQuotedOrBracketed(text: string): string {
  return text
    .replace(/"[^"]*"/g, " ")
    .replace(/'[^']*'/g, " ")
    .replace(/«[^»]*»/g, " ")
    .replace(/„[^“]*“/g, " ")
    .replace(/“[^”]*”/g, " ")
    .replace(/\([^)]*\)/g, " ")
    .replace(/\[[^\]]*\]/g, " ");
}

function isGermanUiText(text: string): boolean {
  const visible = withoutQuotedOrBracketed(text);
  if (ENGLISH_ERROR_WORD.test(visible) && !GERMAN_WORD.test(visible)) return false;
  return GERMAN_UI_TEXT.test(visible);
}

const PG_SQLSTATE = /^[0-9A-Z]{5}$/;

const FETCH_TEXTS = [
  "Failed to fetch",
  "Network request failed",
  "Load failed",
  "NetworkError when attempting to fetch resource",
] as const;

const NET_PREFIX = /^(TypeError|AbortError|TimeoutError):\s*/;

function readErrorName(error: unknown): string {
  if (typeof error === "object" && error !== null && "name" in error) {
    const name = (error as { name?: unknown }).name;
    if (typeof name === "string") return name;
  }
  return "";
}

function readErrorCode(error: unknown): string {
  if (typeof error === "object" && error !== null && "code" in error) {
    const code = (error as { code?: unknown }).code;
    if (typeof code === "string") return code.trim();
  }
  return "";
}

/** Postgres-SQLSTATE, z. B. 25P02 oder 57014. Sonst null. */
export function postgresSqlState(error: unknown): string | null {
  const code = readErrorCode(error);
  return PG_SQLSTATE.test(code) ? code : null;
}

const NAMED_FETCH_ERRORS = new Set(["AuthRetryableFetchError", "FunctionsFetchError"]);

/**
 * Gemeinsame Netzfehler-Regel für Queue und Anzeige.
 * Name oder Präfix TypeError/AbortError/TimeoutError, bei TypeError nur
 * bekannte Fetch-Texte. AuthRetryableFetchError und FunctionsFetchError
 * zählen über error.name. Ein SQLSTATE ist nie ein Netzfehler.
 * Keine Textsuche nach „aborted“ oder „timeout“.
 */
export function isNetworkError(error: unknown): boolean {
  if (postgresSqlState(error)) return false;
  const name = readErrorName(error);
  if (NAMED_FETCH_ERRORS.has(name)) return true;
  const message = readErrorMessage(error).trim();
  const prefix = NET_PREFIX.exec(message);
  const kind =
    name === "TypeError" || name === "AbortError" || name === "TimeoutError"
      ? name
      : prefix?.[1];
  if (kind === "AbortError" || kind === "TimeoutError") return true;
  if (kind !== "TypeError") return false;
  return FETCH_TEXTS.some((text) => message.includes(text));
}

/**
 * Netzfehler werden zur neutralen Meldung, bekannte englische Servertexte
 * deutsch, schon deutsche Texte bleiben, alles andere wird der Fallback.
 * Der Offline-Sync-Hinweis kommt nicht aus dem Fehlertext.
 */
export function uiErrorMessage(error: unknown, fallback: string): string {
  if (isNetworkError(error)) return NO_CONNECTION_MESSAGE;
  const raw = readErrorMessage(error).trim();
  if (!raw) return fallback;
  const mapped = mapDateServerMessage(raw);
  if (mapped !== raw) return mapped;
  if (isGermanUiText(raw)) return raw;
  return fallback;
}

/** Aufnahme: gleiche Regeln wie uiErrorMessage, mit festem Fallback der Maske. */
export function intakeErrorMessage(error: unknown, fallback: string): string {
  return uiErrorMessage(error, fallback);
}

type IsoParts = { y: number; m: number; d: number };

function readIsoParts(value: string): IsoParts | null {
  const match = ISO_DATE_RE.exec(value);
  if (!match) return null;
  return {
    y: Number(match[1]),
    m: Number(match[2]),
    d: Number(match[3]),
  };
}

/**
 * Echtes UTC-Kalenderdatum.
 * setUTCFullYear lässt die Jahreszahl stehen. Date.UTC(y, …) würde y 0–99
 * auf 1900–1999 umbiegen und den Vergleich mit der Eingabe verfälschen.
 */
function isRealUtcCalendarDate(y: number, month: number, day: number): boolean {
  if (!Number.isInteger(y) || month < 1 || month > 12 || day < 1 || day > 31) {
    return false;
  }
  const dt = new Date(0);
  dt.setUTCFullYear(y, month - 1, day);
  return (
    dt.getUTCFullYear() === y &&
    dt.getUTCMonth() === month - 1 &&
    dt.getUTCDate() === day
  );
}

export function isStrictIsoDate(value: string | null | undefined): boolean {
  if (value == null) return true;
  const trimmed = value.trim();
  if (trimmed === "") return true;
  const parts = readIsoParts(trimmed);
  if (!parts) return false;
  if (parts.y < FOUND_ON_MIN_YEAR) return false;
  return isRealUtcCalendarDate(parts.y, parts.m, parts.d);
}

/**
 * Fehlertext für eine getippte Eingabe.
 * null heißt: vollständig, kalendarisch gültig, Jahr >= 1900 und nicht nach maxIso.
 * Leerer String ist kein Fehler (Feld leeren).
 */
export function isoDateInputError(
  value: string,
  maxIso?: string | null
): string | null {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const parts = readIsoParts(trimmed);
  if (!parts || !isRealUtcCalendarDate(parts.y, parts.m, parts.d)) {
    return INVALID_DATE_ERROR;
  }
  if (parts.y < FOUND_ON_MIN_YEAR) {
    return DATE_BEFORE_MIN_YEAR_ERROR;
  }
  if (maxIso && isStrictIsoDate(maxIso) && trimmed > maxIso) {
    return FUTURE_DATE_ERROR;
  }
  return null;
}

/** Heutiges Datum in lokaler Zeitzone als YYYY-MM-DD. */
export function todayIsoLocal(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function compareIsoDates(a: string, b: string): number {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

/** Leerer String → null; Formatprüfung ohne Fachregeln außer der Jahresuntergrenze. */
export function parseOptionalIsoDate(
  value: string | null | undefined
): { ok: true; date: string | null } | { ok: false; error: string } {
  if (value == null || value.trim() === "") {
    return { ok: true, date: null };
  }
  const trimmed = value.trim();
  if (!isStrictIsoDate(trimmed)) {
    return {
      ok: false,
      error: INVALID_DATE_ERROR,
    };
  }
  return { ok: true, date: trimmed };
}

/**
 * Funddatum: Format + kein Zukunftsdatum.
 * Jahr < 1900 → Fehler. Jahr < 1990 → Warnung (Speichern trotzdem erlaubt).
 */
export function assessFoundOn(
  value: string | null | undefined,
  today: string = todayIsoLocal()
): {
  ok: boolean;
  date: string | null;
  error?: string;
  warning?: string;
} {
  if (value != null && value.trim() !== "") {
    const parts = readIsoParts(value.trim());
    if (
      parts &&
      parts.y < FOUND_ON_MIN_YEAR &&
      isRealUtcCalendarDate(parts.y, parts.m, parts.d)
    ) {
      return {
        ok: false,
        date: null,
        error: FOUND_ON_BEFORE_MIN_YEAR_ERROR,
      };
    }
  }
  const parsed = parseOptionalIsoDate(value);
  if (parsed.ok === false) {
    return { ok: false, date: null, error: parsed.error };
  }
  if (parsed.date == null) {
    return { ok: true, date: null };
  }
  if (compareIsoDates(parsed.date, today) > 0) {
    return {
      ok: false,
      date: parsed.date,
      error: FOUND_ON_FUTURE_ERROR,
    };
  }
  const year = Number(parsed.date.slice(0, 4));
  if (year < FOUND_ON_MIN_YEAR) {
    return {
      ok: false,
      date: null,
      error: FOUND_ON_BEFORE_MIN_YEAR_ERROR,
    };
  }
  if (year < FOUND_ON_WARN_BEFORE_YEAR) {
    return {
      ok: true,
      date: parsed.date,
      warning: `Das Funddatum liegt vor ${FOUND_ON_WARN_BEFORE_YEAR}. Bitte prüfen, ob das stimmt.`,
    };
  }
  return { ok: true, date: parsed.date };
}

export function formatIsoDateDE(iso: string | null | undefined): string {
  if (!iso) return "–";
  const parts = readIsoParts(iso.trim());
  if (!parts || !isRealUtcCalendarDate(parts.y, parts.m, parts.d)) return "–";
  const dd = String(parts.d).padStart(2, "0");
  const mm = String(parts.m).padStart(2, "0");
  const yyyy = String(parts.y).padStart(4, "0");
  return `${dd}.${mm}.${yyyy}`;
}

/** Anzeige-Zeitzone für Datum und Uhrzeit. */
export const DISPLAY_TIME_ZONE = "Europe/Berlin";

const MONTHS_DE = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];

const WEEKDAYS_DE = [
  "Sonntag",
  "Montag",
  "Dienstag",
  "Mittwoch",
  "Donnerstag",
  "Freitag",
  "Samstag",
];

const WEEKDAYS_SHORT_DE = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

export type BerlinClock = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
};

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

/** Kalenderteile in Europe/Berlin, unabhängig von der Geräte-Locale. */
export function berlinClock(value: string | Date): BerlinClock | null {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: DISPLAY_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const pick = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? "";
  const hourRaw = pick("hour");
  return {
    year: Number(pick("year")),
    month: Number(pick("month")),
    day: Number(pick("day")),
    hour: hourRaw === "24" ? 0 : Number(hourRaw),
    minute: Number(pick("minute")),
  };
}

function weekdayIndex(clock: BerlinClock): number {
  return new Date(Date.UTC(clock.year, clock.month - 1, clock.day)).getUTCDay();
}

function berlinDayNumber(clock: BerlinClock): number {
  return Math.round(Date.UTC(clock.year, clock.month - 1, clock.day) / 86_400_000);
}

/** Kalendertag: ISO-Datum bleibt der gespeicherte Tag, Zeitstempel in Europe/Berlin. */
export function formatDateDE(value: string | Date | null | undefined): string {
  if (value == null || value === "") return "–";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    return formatIsoDateDE(value);
  }
  const clock = berlinClock(value);
  if (!clock) return "–";
  return `${pad2(clock.day)}.${pad2(clock.month)}.${pad2(clock.year)}`;
}

export function formatTimeDE(value: string | Date | null | undefined): string {
  if (value == null || value === "") return "–";
  const clock = berlinClock(value);
  if (!clock) return "–";
  return `${pad2(clock.hour)}:${pad2(clock.minute)}`;
}

/** Datum und Uhrzeit, z. B. 14.10.2026, 10:30. */
export function formatTimestampDE(value: string | Date | null | undefined): string {
  if (value == null || value === "") return "–";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    return formatIsoDateDE(value);
  }
  const clock = berlinClock(value);
  if (!clock) return "–";
  return `${formatDateDE(value)}, ${pad2(clock.hour)}:${pad2(clock.minute)}`;
}

export function formatMonthYearDE(year: number, monthIndex: number): string {
  const name = MONTHS_DE[monthIndex] ?? "";
  return `${name} ${year}`.trim();
}

export function formatLongDateDE(value: string | Date | null | undefined): string {
  if (value == null || value === "") return "–";
  const clock =
    typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())
      ? (() => {
          const parts = readIsoParts(value.trim());
          if (!parts) return null;
          return {
            year: parts.y,
            month: parts.m,
            day: parts.d,
            hour: 0,
            minute: 0,
          };
        })()
      : berlinClock(value);
  if (!clock) return "–";
  const weekday = WEEKDAYS_DE[weekdayIndex(clock)] ?? "";
  const month = MONTHS_DE[clock.month - 1] ?? "";
  return `${weekday}, ${clock.day}. ${month} ${clock.year}`;
}

export function formatAgendaWhenDE(iso: string, allDay: boolean): string {
  const clock = berlinClock(iso);
  if (!clock) return "–";
  const weekday = WEEKDAYS_SHORT_DE[weekdayIndex(clock)] ?? "";
  const month = MONTHS_DE[clock.month - 1] ?? "";
  if (allDay) return `${weekday}, ${clock.day}. ${month}`;
  return `${weekday}, ${clock.day}. ${month}, ${pad2(clock.hour)}:${pad2(clock.minute)}`;
}

/** Heute/Gestern nach Europe/Berlin, sonst deutsches Datum. */
export function formatRelativeDayDE(
  iso: string,
  now: Date = new Date(),
): string {
  const clock = berlinClock(iso);
  const today = berlinClock(now);
  if (!clock || !today) return "–";
  const diff = berlinDayNumber(today) - berlinDayNumber(clock);
  if (diff === 0) return "Heute";
  if (diff === 1) return "Gestern";
  return formatDateDE(iso);
}
